import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@material-ui/core'
import DeleteIcon from '@material-ui/icons/Delete'
import EditIcon from '@material-ui/icons/Edit'
import React, { useEffect, useState } from 'react'

import {
  createPlannerActivity,
  deletePlannerActivity,
  fetchPlannerActivity,
  fetchPlannerGradeGroup,
  fetchPlannerGradeSubject,
  fetchPlannerSubject,
  importPlannerCsv,
  updatePlannerActivity
} from 'api'
import { showToast } from 'app/helpers'

const items = (d) =>
  d && Array.isArray(d.results) ? d.results : Array.isArray(d) ? d : []

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
  'August', 'September', 'October', 'November', 'December']

const linesToList = (s) => (s || '').split('\n').map((x) => x.trim()).filter(Boolean)
const listToLines = (l) => (Array.isArray(l) ? l : []).join('\n')
const stepsToText = (steps) =>
  (Array.isArray(steps) ? steps : []).map((s) => `${s.label || 'Step'}: ${s.text || ''}`).join('\n')
const textToSteps = (s) =>
  linesToList(s).map((line) => {
    const i = line.indexOf(':')
    return i < 0 ? { label: 'Step', text: line } : { label: line.slice(0, i).trim(), text: line.slice(i + 1).trim() }
  })

const blankForm = () => ({
  uuid: null, title: '', subject: '', month: '', week: '',
  outcomes: '', materials: '', steps: ''
})

/** Lesson-plan repository: per-grade activities + add/edit + CSV import. */
const Repository = () => {
  const [grades, setGrades] = useState([])
  const [gradeIdx, setGradeIdx] = useState(0)
  const [subjects, setSubjects] = useState([]) // [{uuid,label}] for this grade
  const [labelByUuid, setLabelByUuid] = useState({})
  const [filters, setFilters] = useState({ subject: '', month: '', week: '' })
  const [activities, setActivities] = useState([])
  const [loading, setLoading] = useState(false)
  const [form, setForm] = useState(null) // null = dialog closed
  const [csv, setCsv] = useState({ open: false, text: '', result: null, busy: false })

  const grade = grades[gradeIdx]

  useEffect(() => {
    fetchPlannerGradeGroup({})
      .then((g) => setGrades(items(g)))
      .catch(() => showToast('Failed to load grades', 'danger'))
    fetchPlannerSubject({})
      .then((s) => {
        const m = {}
        items(s).forEach((x) => { m[x.uuid] = x.label })
        setLabelByUuid(m)
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    if (!grade) return
    fetchPlannerGradeSubject({ queries: { grade_group: grade.uuid } })
      .then((gs) => setSubjects(items(gs).map((l) => ({ uuid: l.subject, label: labelByUuid[l.subject] || l.subject_label || '—' }))))
      .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grade && grade.uuid, labelByUuid])

  const reload = async () => {
    if (!grade) return
    setLoading(true)
    try {
      const q = { grade_group: grade.uuid }
      if (filters.subject) q.subject = filters.subject
      if (filters.month) q.month = filters.month
      if (filters.week) q.week = filters.week
      setActivities(items(await fetchPlannerActivity({ queries: q })))
    } catch (e) {
      showToast('Failed to load activities', 'danger')
    }
    setLoading(false)
  }

  useEffect(() => {
    reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grade && grade.uuid, filters.subject, filters.month, filters.week])

  const openAdd = () => setForm({ ...blankForm(), subject: filters.subject || (subjects[0] && subjects[0].uuid) || '' })
  const openEdit = (a) => setForm({
    uuid: a.uuid, title: a.title, subject: a.subject, month: a.month || '', week: a.week || '',
    outcomes: listToLines(a.outcomes), materials: listToLines(a.materials), steps: stepsToText(a.steps)
  })

  const save = async () => {
    if (!form.title || !form.subject) {
      showToast('Title and subject are required', 'danger')
      return
    }
    const payload = {
      uuid: form.uuid, grade_group: grade.uuid, subject: form.subject, title: form.title,
      month: form.month || null, week: form.week || null,
      outcomes: linesToList(form.outcomes), materials: linesToList(form.materials),
      steps: textToSteps(form.steps)
    }
    try {
      if (form.uuid) await updatePlannerActivity(payload)
      else await createPlannerActivity(payload)
      setForm(null)
      await reload()
      showToast('Activity saved', 'success')
    } catch (e) {
      showToast('Failed to save activity', 'danger')
    }
  }

  const del = async (a) => {
    try {
      await deletePlannerActivity({ ids: [a.uuid] })
      await reload()
    } catch (e) {
      showToast('Failed to delete activity', 'danger')
    }
  }

  const runCsv = async () => {
    setCsv((c) => ({ ...c, busy: true }))
    try {
      const result = await importPlannerCsv(csv.text)
      setCsv((c) => ({ ...c, result, busy: false }))
      await reload()
    } catch (e) {
      showToast('CSV import failed', 'danger')
      setCsv((c) => ({ ...c, busy: false }))
    }
  }

  const onFile = (e) => {
    const file = e.target.files && e.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => setCsv((c) => ({ ...c, text: String(reader.result || '') }))
    reader.readAsText(file)
  }

  return (
    <Box p={2}>
      <Typography variant="h5" gutterBottom>Lesson Plan Repository</Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Manage activities per grade group. Teachers see only activities for their grade.
      </Typography>

      <Tabs value={gradeIdx} onChange={(e, v) => setGradeIdx(v)} indicatorColor="primary"
        textColor="primary" variant="scrollable">
        {grades.map((g) => <Tab key={g.uuid} label={`${g.emoji || ''} ${g.name}`} />)}
      </Tabs>

      <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 12 }} mt={2} mb={2}>
        <FormControl variant="outlined" size="small" style={{ minWidth: 160 }}>
          <InputLabel>Subject</InputLabel>
          <Select label="Subject" value={filters.subject}
            onChange={(e) => setFilters((f) => ({ ...f, subject: e.target.value }))}>
            <MenuItem value=""><em>All</em></MenuItem>
            {subjects.map((s) => <MenuItem key={s.uuid} value={s.uuid}>{s.label}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl variant="outlined" size="small" style={{ minWidth: 140 }}>
          <InputLabel>Month</InputLabel>
          <Select label="Month" value={filters.month}
            onChange={(e) => setFilters((f) => ({ ...f, month: e.target.value }))}>
            <MenuItem value=""><em>All</em></MenuItem>
            {MONTHS.map((m, i) => <MenuItem key={m} value={i + 1}>{m}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl variant="outlined" size="small" style={{ minWidth: 120 }}>
          <InputLabel>Week</InputLabel>
          <Select label="Week" value={filters.week}
            onChange={(e) => setFilters((f) => ({ ...f, week: e.target.value }))}>
            <MenuItem value=""><em>All</em></MenuItem>
            {[1, 2, 3, 4, 5].map((w) => <MenuItem key={w} value={w}>{`Week ${w}`}</MenuItem>)}
          </Select>
        </FormControl>
        <Box flexGrow={1} />
        <Button variant="outlined" onClick={() => setCsv((c) => ({ ...c, open: true, result: null }))}>
          Upload CSV
        </Button>
        <Button variant="contained" color="primary" onClick={openAdd} disabled={!subjects.length}>
          + Add Activity
        </Button>
      </Box>

      {loading ? <CircularProgress /> : (
        <Box>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            {activities.length} activities
          </Typography>
          {activities.map((a) => (
            <Paper key={a.uuid} variant="outlined"
              style={{ padding: '8px 12px', marginBottom: 8, display: 'flex', alignItems: 'center' }}>
              <Chip size="small" label={labelByUuid[a.subject] || a.subject_label || ''}
                style={{ marginRight: 12 }} />
              <Typography variant="body1" style={{ flexGrow: 1 }}>{a.title}</Typography>
              <IconButton size="small" onClick={() => openEdit(a)}><EditIcon fontSize="small" /></IconButton>
              <IconButton size="small" onClick={() => del(a)}><DeleteIcon fontSize="small" /></IconButton>
            </Paper>
          ))}
          {!activities.length && (
            <Typography variant="body2" color="textSecondary">No activities match these filters.</Typography>
          )}
        </Box>
      )}

      {/* Add / Edit dialog */}
      <Dialog open={!!form} onClose={() => setForm(null)} fullWidth maxWidth="sm">
        <DialogTitle>{form && form.uuid ? 'Edit activity' : 'Add activity'}</DialogTitle>
        <DialogContent>
          {form && (
            <Box display="flex" flexDirection="column" style={{ gap: 12 }} pt={1}>
              <TextField label="Title" value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
              <FormControl>
                <InputLabel>Subject</InputLabel>
                <Select value={form.subject}
                  onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}>
                  {subjects.map((s) => <MenuItem key={s.uuid} value={s.uuid}>{s.label}</MenuItem>)}
                </Select>
              </FormControl>
              <Box display="flex" style={{ gap: 12 }}>
                <FormControl style={{ flex: 1 }}>
                  <InputLabel>Month</InputLabel>
                  <Select value={form.month}
                    onChange={(e) => setForm((f) => ({ ...f, month: e.target.value }))}>
                    <MenuItem value=""><em>Unassigned</em></MenuItem>
                    {MONTHS.map((m, i) => <MenuItem key={m} value={i + 1}>{m}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl style={{ flex: 1 }}>
                  <InputLabel>Week of month</InputLabel>
                  <Select value={form.week}
                    onChange={(e) => setForm((f) => ({ ...f, week: e.target.value }))}>
                    <MenuItem value=""><em>Unassigned</em></MenuItem>
                    {[1, 2, 3, 4, 5].map((w) => <MenuItem key={w} value={w}>{`Week ${w}`}</MenuItem>)}
                  </Select>
                </FormControl>
              </Box>
              <TextField label="Learning outcomes (one per line)" multiline minRows={2}
                value={form.outcomes} onChange={(e) => setForm((f) => ({ ...f, outcomes: e.target.value }))} />
              <TextField label="Materials (one per line)" multiline minRows={2}
                value={form.materials} onChange={(e) => setForm((f) => ({ ...f, materials: e.target.value }))} />
              <TextField label="Steps (one per line — 'Label: Text')" multiline minRows={3}
                value={form.steps} onChange={(e) => setForm((f) => ({ ...f, steps: e.target.value }))} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForm(null)}>Cancel</Button>
          <Button color="primary" variant="contained" onClick={save}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* CSV import dialog */}
      <Dialog open={csv.open} onClose={() => setCsv((c) => ({ ...c, open: false }))} fullWidth maxWidth="sm">
        <DialogTitle>Bulk import activities (CSV)</DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="textSecondary">
            Columns: grade_group, type, title, month, week, outcomes(|), materials(|),
            steps(Label: Text || …), image_urls(|). Lines starting with # are ignored.
          </Typography>
          <Box mt={1} mb={1}>
            <input type="file" accept=".csv,text/csv" onChange={onFile} />
          </Box>
          <TextField fullWidth multiline minRows={5} placeholder="…or paste CSV here"
            value={csv.text} onChange={(e) => setCsv((c) => ({ ...c, text: e.target.value }))} />
          {csv.result && (
            <Box mt={1}>
              <Typography variant="body2">
                Created {csv.result.created}, updated {csv.result.updated}, {(csv.result.errors || []).length} errors.
              </Typography>
              {(csv.result.errors || []).slice(0, 8).map((er, i) => (
                <Typography key={i} variant="caption" color="error" display="block">
                  Row {er.row}: {er.error}
                </Typography>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCsv((c) => ({ ...c, open: false }))}>Close</Button>
          <Button color="primary" variant="contained" onClick={runCsv} disabled={csv.busy || !csv.text.trim()}>
            {csv.busy ? 'Importing…' : 'Import'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default Repository
